import { Col, Container, Row } from 'react-bootstrap'
import { NetworkStatus, useQuery } from '@apollo/client'
import { observer } from 'mobx-react'

import { Card } from 'Components/_theme'
import { FaqCard } from 'Components/FaqCard/FaqCard'
import { ImpactTracker } from 'Components/ImpactTracker/ImpactTracker'
import { InfiniteScrollLoadingState } from 'Components/InfiniteScrollLoadingState/InfiniteScrollLoadingState'
import { ListSearch } from 'Components/ListSearch/ListSearch'
import { ObserveMeOnceIAmMounted } from 'Components/ObserveMeOnceIAmMounted/ObserveMeOnceIAmMounted'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageHeader } from 'Components/PageHeader/PageHeader'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'

import { useActiveUser } from 'Hooks'

import { PAGINATION } from 'Constants/constants'
import type {
  getOrganisationDepots,
  getOrganisationDepotsVariables,
} from 'Constants/graphql/queries/listing/__generated__/getOrganisationDepots'
import { GET_ORGANISATION_DEPOTS } from 'Constants/graphql/queries/listing/GetDepots'
import { PortalSectionEnum } from 'Constants/portalSection'

import { DepotsTable } from './DepotsTable'

export const Depots = observer(() => {
  const { activeOrganisation } = useActiveUser()

  const {
    loading: isLoading,
    data,
    error,
    fetchMore,
    refetch,
    networkStatus,
  } = useQuery<getOrganisationDepots, getOrganisationDepotsVariables>(
    GET_ORGANISATION_DEPOTS,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      variables: {
        after: PAGINATION.AFTER,
        first: PAGINATION.ITEMS_PER_PAGE,
        id: activeOrganisation?.id!,
      },
    },
  )

  const organisationDepots: getOrganisationDepots | undefined = data

  const onLoadMore = () => {
    fetchMore({
      variables: {
        after: organisationDepots?.organisation.depots?.pageInfo.endCursor,
      },
    })
  }

  const handleSearch = (value: string) => {
    refetch({
      searchterm: value || null,
    })
  }

  const {
    depots,
    pageInfo: { hasNextPage },
  } = {
    ...data?.organisation?.depots,
    pageInfo: {
      hasNextPage: false,
      ...data?.organisation?.depots?.pageInfo,
    },
  }

  const intersectionObserver = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      onLoadMore()
    }
  })

  if (error) {
    return <PageErrorState />
  }

  if (isLoading && !data) {
    return <PageLoadingState />
  }

  return (
    <Container className="pt-6" fluid>
      <div role="banner">
        <PageHeader portalSection={PortalSectionEnum.Depots} />
      </div>

      <Row>
        <Col xl={12} xxl={8}>
          <Card className="mb-4">
            <ListSearch
              buttonVisible={false}
              handleSearch={handleSearch}
              totalCount={depots?.length ?? 0}
            />

            <Row>
              <Col>
                <DepotsTable
                  depots={depots ?? []}
                  isLoading={isLoading}
                  isSearching={networkStatus === NetworkStatus.setVariables}
                />
              </Col>
            </Row>
          </Card>

          <Row className="p-1">
            <Col>
              {hasNextPage &&
                (data && isLoading ? (
                  <InfiniteScrollLoadingState />
                ) : (
                  <ObserveMeOnceIAmMounted
                    intersectionObserver={intersectionObserver}
                  />
                ))}
            </Col>
          </Row>
        </Col>

        <Col xl={12} xxl={4}>
          <div className="sticky-column">
            <ImpactTracker />
            <FaqCard />
          </div>
        </Col>
      </Row>
    </Container>
  )
})
